/**
 * MAP enforcement rule inspect view per scope §6.6.1 (post scope-doc
 * clarification commit 2868958). Atlas Day 3a P5.
 *
 * Cross-table substrate explicit at top of inspect: brands.enforces_map +
 * products.map_price. Engine reads: brands.enforces_map = true AND
 * products.map_price IS NOT NULL AND sale_price < products.map_price →
 * below_map=true (verified day66Schema.test.ts:148-149).
 *
 * Layer content surfaces cross-table semantics throughout per scope §6.6.1
 * L4-L11. Split-by-substrate L4/L5/L6 patterns (three sub-patterns within
 * layers as needed).
 *
 * Cross-link: Directory → Brands (per-brand enforces_map edit surface).
 * Per-product map_price not Atlas-editable — Algopix-sourced; surfaces
 * in inspect trace only.
 */

import type { JSX } from 'react';
import { URL_DIRECTORY_BRANDS } from '../../../lib/atlas/atlasUrls';
import { MAP_ENFORCEMENT_PROPAGATION } from '../../../lib/atlas/propagationMaps';
import InspectShell from './InspectShell';
import LayerSection from './LayerSection';
import ComposabilityReservedLayer from './ComposabilityReservedLayer';

export default function MapEnforcementInspect(): JSX.Element {
  return (
    <InspectShell
      subRegionName="MAP enforcement"
      surfaceLine="ISG enforces MAP on N of M brands. Flag-and-report posture — engine surfaces violations to buyers rather than blocking sales."
    >
      {/* Cross-table substrate reference per scope §6.6.1 Substrate line */}
      <div className="bg-surface-1 border border-border-subtle rounded-lg p-3">
        <h2 className="text-xs font-semibold text-ink-bright uppercase tracking-wide mb-1">
          Cross-table substrate
        </h2>
        <p className="text-xs text-ink-secondary">
          <code>brands.enforces_map</code> (boolean, per-brand enforcement stance) +{' '}
          <code>products.map_price</code> (numeric, per-product MAP value, Algopix-sourced).
          Engine reads:{' '}
          <code>
            brands.enforces_map = true AND products.map_price IS NOT NULL AND sale_price &lt;
            products.map_price → below_map=true
          </code>
          .
        </p>
      </div>

      <LayerSection layer={4} title="Math vs judgment">
        <p>Per scope §6.6.1 — split per substrate:</p>
        <ul className="list-disc pl-5 text-xs space-y-1">
          <li>
            <strong>Judgment-pattern</strong> for <code>brands.enforces_map</code> —
            enforcing MAP on these brands is ISG&apos;s choice; flag-and-report posture is
            ISG&apos;s choice per ADR 41 Shape B.
          </li>
          <li>
            <strong>Math-pattern</strong> for the cross-table comparison itself —{' '}
            <code>sale_price &lt; products.map_price</code> arithmetic.
          </li>
          <li>
            <strong>Mechanical-pattern</strong> for <code>products.map_price</code> substrate
            (Algopix-sourced; ISG records the brand-published MAP value, doesn&apos;t choose
            it).
          </li>
        </ul>
      </LayerSection>

      <LayerSection layer={5} title="Mechanical vs strategic">
        <p>Split per substrate per scope §6.6.1 L5:</p>
        <ul className="list-disc pl-5 text-xs space-y-1">
          <li>
            <code>brands.enforces_map</code> = <strong>strategic</strong> (ISG&apos;s
            enforcement choice)
          </li>
          <li>
            <code>products.map_price</code> = <strong>mechanical</strong> (brand-published
            value ISG records)
          </li>
        </ul>
      </LayerSection>

      <LayerSection layer={6} title="Operational vs ownership">
        <p>Split per substrate per scope §6.6.1 L6:</p>
        <ul className="list-disc pl-5 text-xs space-y-1">
          <li>
            <code>brands.enforces_map</code> = <strong>ownership-altitude</strong> (defines
            per-brand MAP enforcement stance)
          </li>
          <li>
            <code>products.map_price</code> = <strong>layer 6 N/A</strong> at value-altitude
            (mechanical-fact; surfaces at region-altitude per three-state layer applicability)
          </li>
        </ul>
      </LayerSection>

      <LayerSection layer={7} title="Trace">
        <p>Cross-table trace per scope §6.6.1 L7:</p>
        <ul className="list-disc pl-5 text-xs space-y-1">
          <li>
            <code>brands.provenance</code> covers <code>enforces_map</code> changes (Day 67
            P2.2 substrate + Day 82 P6.1 triggers cover enforces_map)
          </li>
          <li>
            <code>products.provenance</code> covers <code>map_price</code> changes (per-product,
            Algopix-sourced via useAlgopixImport with provenance write per Atlas Day 2 Phase 0
            #11.6 evidence)
          </li>
        </ul>
      </LayerSection>

      <LayerSection layer={8} title="Propagation">
        <p>
          Curated propagation map (verified as of 2026-05-27 per scope §8 staleness marker):
        </p>
        <ul className="list-disc pl-5 text-xs space-y-2">
          {MAP_ENFORCEMENT_PROPAGATION.map((entry) => (
            <li key={entry.field}>
              <strong>
                <code>{entry.field}</code>
              </strong>{' '}
              edits affect:
              <ul className="list-circle pl-5 mt-1 space-y-0.5">
                {entry.affects.map((eff) => (
                  <li key={eff}>{eff}</li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </LayerSection>

      <LayerSection layer={9} title="Reversibility">
        <p>
          Reversible-with-residue per scope §6.6.1 L9. Reversible going forward. Historical
          records preserve prior-value state — past <code>below_map</code> flags persist on
          products from prior enforcement window.
        </p>
      </LayerSection>

      <ComposabilityReservedLayer />

      <LayerSection layer={11} title="Positional contribution">
        <p>
          Substantive-contribution per scope §6.6.1 L11. Surfaces in title block sentence 2
          MAP-enforcement clause (substrate-driven per current brand count; Day 1 D5
          conditional-render pattern applies — current substrate state per Atlas Day 2 Phase 0
          #10 = 0 enforces_map brands, so title block surfaces &quot;has not yet configured
          MAP enforcement&quot;).
        </p>
      </LayerSection>

      <LayerSection layer={4 /* cross-links footer */} title="Cross-links">
        <p>
          Per scope §6.6.1: Directory → Brands (per-brand <code>enforces_map</code> edit
          surface). Per-product <code>map_price</code> not Atlas-editable — Algopix-sourced;
          surfaces in L7 trace only.
        </p>
        <ul className="list-disc pl-5 text-xs">
          <li>
            <a href={URL_DIRECTORY_BRANDS} className="text-accent hover:underline">
              Directory → Brands
            </a>
          </li>
        </ul>
      </LayerSection>
    </InspectShell>
  );
}
